/**
 * Segment inspector (W2-06; DESIGN.md §4.11, §5.6.2, §5.6.5, §6.4, §6.11). A segment runs from a keyframe (its
 * start key, which carries the curve) to the next keyframe. `SegmentBody` is shared with the key section (the
 * outgoing segment of a single keyframe):
 *  - curve with plain words (Guided) or RPE names (Full), `Change...` opens the easing picker (W2-05) for every
 *    segment and applies the result with Core.keys.setEase (guards per use; skipped segments reported);
 *  - "Stay, then jump" toggle, from / to values (to is fixed after a Stay), slice "Use part of the curve" (Full);
 *  - Bake / Reverse / Extrapolate / Link (W2-11 commands, run through the registry on the selection), link group;
 *  - exactness: Phira / RPE line, "within 0.1 %" for split Expo / Elastic curves, NaN slices, curves RPE draws
 *    differently, eased Classic speed (baked on export) and preserved RPE easings Phira ignores.
 */

import { useRef, useState } from 'react';
import type { AppServices, InspectorSectionDef, InspectorSectionProps } from '../../../app/registry/types';
import { DIALOG } from '../../../app/dialogs/types';
import { Core } from '../../../core/commands/index';
import { setKeySide } from '../../../core/commands/keys';
import type { ExprProgram } from '../../../core/expr/types';
import type { Ease as EaseValue } from '../../../core/ease/types';
import type { ChartDoc, KeyId } from '../../../core/model/types';
import { Beats } from '../../../core/time/beat';
import type { RGB } from '../../../core/util/types';
import { useLocale, useT } from '../../../i18n/useT';
import type { DocStore } from '../../../state/types';
import { Button, Checkbox, ColorField, CurveThumb, Field, NumberField, Section } from '../../../ui';
import { exprKeysShown, setSlice, setStay } from '../commands';
import { useDocValue } from '../hooks';
import { continuityBefore, reportJumpsMade } from '../jumpToast';
import { curveText, easeEq, fmtList, propLabelKey } from '../labels';
import { m } from '../inspector.messages';
import { checkOf, easeUseFor, fieldOf, fieldOr, rgbEq, segmentExactness, sortedIds, textModeOf } from '../model';
import {
  Actions,
  Callout,
  crossScreenText,
  fmtNum,
  Readout,
  Row,
  Title,
  useSectionBase,
  useStore,
  WithInfo,
} from '../parts';
import s from '../Inspector.module.css';
import { newSeed, useEditKit } from '../tx';
import { FIELD, useFieldProblems } from '../useFieldProblems';
import { commonUnit, unitSetup, useKeyInfos, type KeyInfo } from './keyInfo';

/** One end of a tween slice (0 for curves that cannot be sliced). */
function sliceSide(e: EaseValue, side: 'left' | 'right'): number {
  if (e.kind !== 'tween') return 0;
  return side === 'left' ? e.left : e.right;
}

function speedModeOf(doc: ChartDoc): 'legacy' | 'rpe170' {
  return doc.pkg.speedMode;
}

export interface SegmentBodyProps {
  readonly services: AppServices;
  readonly store: DocStore;
  /** Start keys of the segments (keys without a next key are ignored). */
  readonly segments: readonly KeyId[];
}

/** Fields of one or several segments. */
export function SegmentBody(props: SegmentBodyProps) {
  const { services, store } = props;
  const t = useT();
  const locale = useLocale();
  const base = useSectionBase(services, store);
  const all = useKeyInfos(store, props.segments);
  const infos = all.filter((i) => i.ctx.next !== null);
  const ids = infos.map((i) => i.ctx.key.id);
  const nextIds = infos.map((i) => (i.ctx.next as NonNullable<KeyInfo['ctx']['next']>).id);
  const edit = useEditKit(services, store, `segments:${ids.join(',')}`);
  const badges = useFieldProblems(services, { keys: [...ids, ...nextIds] });
  const speedMode = useDocValue(store, speedModeOf, ['pkg']);
  const [seed, setSeed] = useState(newSeed);
  // Continuity before a From / To scrub gesture started (UX-033: the gesture replaces its pending ops, so the
  // state at the first step is the one to compare against for the "Made a jump" toast).
  const scrubBefore = useRef<Map<KeyId, boolean> | null>(null);
  const first = infos[0];
  if (first === undefined) return <Readout>{t(m.noSegment)}</Readout>;

  const eases = infos.map((i) => i.ctx.key.ease);
  const ease = fieldOf(eases, easeEq);
  const stay = checkOf(eases.map((e) => e.kind === 'hold'));
  const allHold = stay === true;
  const tweens = eases.every((e) => e.kind === 'tween');
  // A slice ("Use part of the curve") is in use when some end is not the full [0, 1] part (CR-W3-01-12).
  const sliceInUse = eases.some((e) => e.kind === 'tween' && (e.left !== 0 || e.right !== 1));
  const { unit, mixed: unitMixed } = commonUnit(infos);
  const allNumbers = infos.every((i) => i.valueType === 'number');
  const allColours = infos.every((i) => i.valueType === 'rgb');
  const isText = infos.every((i) => i.ctx.prop === 'text');
  const textMode =
    first.ctx.prop === 'text' && first.ctx.next !== null
      ? textModeOf(first.ctx.key.vOut as string, first.ctx.next.vIn as string, first.ctx.key.ease)
      : null;
  const use = easeUseFor(first.ctx.prop, first.ctx.track.owner.kind, speedMode, base.level, textMode);
  const afterExpr = (v: { readonly kind: string; readonly program?: ExprProgram }): void => {
    if (v.kind === 'expr' && v.program?.usesRand === true) setSeed(newSeed());
  };

  // ---- exactness (worst over the selection)
  const exact = infos.map((i) =>
    segmentExactness(i.ctx.key, i.ctx.prop, speedMode, { prev: i.ctx.prev, next: i.ctx.next }),
  );
  const phiraNan = exact.some((x) => x.phira === 'nan');
  const baked = exact.some((x) => x.phira === 'baked');
  const approx = exact.some((x) => x.approxSplit);
  const differs = infos.filter((_, i) => exact[i]?.rpe === 'differs');
  const ignored = infos.filter((_, i) => exact[i]?.rpe === 'ignored');
  const phiraState = phiraNan ? 'nan' : baked ? 'baked' : 'exact';
  const rpeState = differs.length > 0 ? 'differs' : ignored.length > 0 ? 'ignored' : baked ? 'baked' : 'exact';
  const links = [...new Set(infos.map((i) => i.ctx.key.link).filter((l) => l !== 0))];

  const openPicker = async (): Promise<void> => {
    const result = await services.ui.openDialog(DIALOG.easingPicker, {
      segments: ids,
      initial: ease !== null && ease.kind === 'value' ? ease.value : null,
      use,
    });
    if (result === null) return;
    const out = edit.once({ key: m.txEase }, (tx) => Core.keys.setEase(tx, { segments: ids, ease: result }));
    if (out.ok && out.value.skipped > 0) {
      services.ui.toast({ key: m.easeSkipped, params: { count: out.value.skipped } }, { kind: 'warning' });
    }
  };

  const numberSide = (side: 'from' | 'to') => {
    const setup = unitSetup(t, services, base, unit);
    const keys = side === 'from' ? ids : nextIds;
    const part = side === 'from' ? 'out' : 'in';
    const raw = infos.map((i) =>
      side === 'from'
        ? (i.ctx.key.vOut as number)
        : ((i.ctx.next as NonNullable<KeyInfo['ctx']['next']>).vIn as number),
    );
    const value = fieldOr(raw.map(setup.toShown), 0);
    const fieldPath = side === 'from' ? FIELD.segmentFrom : FIELD.segmentTo;
    const fixed = side === 'to' && allHold;
    const label = { key: m.txKeyValue };
    return (
      <Row edit={edit} field={fieldPath}>
        <NumberField
          label={t(side === 'from' ? m.segmentFrom : m.segmentTo)}
          stacked
          value={value}
          step={setup.step}
          precision={3}
          vars="segmentField"
          randomSeed={seed}
          disabled={fixed}
          helpId={fieldPath}
          badges={badges(fieldPath)}
          {...setup.unitProps}
          onCommit={(v) => {
            const before = continuityBefore(store.doc, keys);
            if (v.kind === 'number') {
              const r = setup.toRaw(v.value);
              edit.commit(fieldPath, label, (tx) => {
                // UX-033 (CR-W3-05-1): continuity-preserving side edit (RPE autostick) instead of a silent jump.
                setKeySide(tx, { keys, side: part, value: r });
              });
            } else {
              const prog = v.program;
              edit.commit(fieldPath, label, (tx) =>
                exprKeysShown(tx, { keys, part, unit: setup.shown, prog, seed, keepSide: part }),
              );
            }
            reportJumpsMade(services, base.fmtBeat, before, keys);
            afterExpr(v);
          }}
          onScrub={(v, final) => {
            if (scrubBefore.current === null) scrubBefore.current = continuityBefore(store.doc, keys);
            const r = setup.toRaw(v);
            edit.scrub(
              fieldPath,
              label,
              (tx) => {
                setKeySide(tx, { keys, side: part, value: r });
              },
              final,
            );
            if (final) {
              const before = scrubBefore.current;
              scrubBefore.current = null;
              reportJumpsMade(services, base.fmtBeat, before, keys);
            }
          }}
        />
        {fixed && <Readout>{t(m.segmentToFixed)}</Readout>}
      </Row>
    );
  };

  const colourSide = (side: 'from' | 'to') => {
    const keys = side === 'from' ? ids : nextIds;
    const part = side === 'from' ? 'out' : 'in';
    const vals = infos.map((i) =>
      side === 'from' ? (i.ctx.key.vOut as RGB) : ((i.ctx.next as NonNullable<KeyInfo['ctx']['next']>).vIn as RGB),
    );
    const value = fieldOr(vals, [255, 255, 255] as RGB, rgbEq);
    const fieldPath = side === 'from' ? FIELD.segmentFrom : FIELD.segmentTo;
    return (
      <Row edit={edit} field={fieldPath}>
        <ColorField
          label={t(side === 'from' ? m.segmentFrom : m.segmentTo)}
          value={value}
          helpId={fieldPath}
          onCommit={(c) => {
            if (c === null) return;
            const before = continuityBefore(store.doc, keys);
            edit.commit(fieldPath, { key: m.txKeyValue }, (tx) => {
              setKeySide(tx, { keys, side: part, value: c });
            });
            reportJumpsMade(services, base.fmtBeat, before, keys);
          }}
        />
      </Row>
    );
  };

  const span = infos.length === 1 && first.ctx.next !== null ? first.ctx.next : null;
  const durBeats = span === null ? null : Beats.sub(span.t, first.ctx.key.t);
  const durSec = span === null ? null : base.tempo.secAt(span.t) - base.tempo.secAt(first.ctx.key.t);

  return (
    <div className={s.fields}>
      {/* ---------------- Curve */}
      <Row edit={edit} field={FIELD.segmentEase}>
        <Field
          label={t(m.segmentCurve)}
          helpId={FIELD.segmentEase}
          help={{ key: m.segmentCurveHelp }}
          badges={[
            ...badges(FIELD.segmentEase),
            ...(baked ? [{ kind: 'baked' as const }] : []),
            // CR-W3-01-12 (D§2.4): the slice editor is Full-only, so a slice in use carries the badge at Guided.
            ...(base.level === 'guided' && sliceInUse ? [{ kind: 'inUse' as const }] : []),
          ]}
          info={{ text: { key: m.segmentCurveInfo }, rpeTerm: 'easingType', communityTerm: '缓动' }}
        >
          <div className={s.inline}>
            {ease !== null && ease.kind === 'value' && <CurveThumb ease={ease.value} size={28} showOvershoot />}
            <span className={s.grow} data-testid="curve-name">
              {ease === null || ease.kind === 'mixed' ? t(m.curveMixed) : curveText(t, ease.value, base.level)}
            </span>
            <Button
              size="sm"
              icon="Spline"
              onClick={() => {
                void openPicker();
              }}
            >
              {t(m.changeCurve)}
            </Button>
          </div>
        </Field>
      </Row>

      {/* ---------------- Stay */}
      <Row edit={edit} field={FIELD.segmentStay}>
        <WithInfo info={{ text: m.segmentStayInfo, rpe: 'lock', community: '钩定', illustration: 'stayThenJump' }}>
          <Checkbox
            className={s.checkInline}
            label={t(m.segmentStay)}
            checked={stay}
            helpId={FIELD.segmentStay}
            onChange={(on) => {
              edit.once({ key: m.txStay }, (tx) => {
                setStay(tx, { segments: ids, on });
              });
            }}
          />
        </WithInfo>
      </Row>

      {/* ---------------- From / To */}
      {allNumbers && !isText && (
        <div className={s.pairCompact}>
          {numberSide('from')}
          {numberSide('to')}
        </div>
      )}
      {allNumbers && unitMixed && <Readout>{t(m.keyUnitMixed)}</Readout>}
      {allNumbers && unit === 'speed' && infos.length === 1 && (
        <>
          <Readout>
            {t(m.segmentDuration, {
              beats: base.fmtBeat(durBeats ?? Beats.ZERO),
              sec: fmtNum(durSec ?? 0, 3),
            })}
          </Readout>
          <Readout testId="cross-screen">
            {crossScreenText(
              t,
              first.ctx.key.vOut as number,
              // A Stay segment keeps its speed until the next keyframe.
              first.ctx.key.ease.kind === 'hold' || first.ctx.next === null
                ? (first.ctx.key.vOut as number)
                : (first.ctx.next.vIn as number),
            )}
          </Readout>
        </>
      )}
      {allColours && (
        <div className={s.pair}>
          {colourSide('from')}
          {colourSide('to')}
        </div>
      )}

      {/* ---------------- Slice (Full) */}
      {base.level === 'full' && !allHold && (
        <Row edit={edit} field={FIELD.segmentSlice}>
          {tweens && ease !== null ? (
            <>
              <div className={s.inline}>
                <span className={s.subheading}>{t(m.segmentSlice)}</span>
                <WithInfo
                  info={{
                    text: m.segmentSliceInfo,
                    rpe: 'easingLeft / easingRight',
                    community: '缓动切割',
                    illustration: 'easingSlice',
                  }}
                >
                  <span />
                </WithInfo>
              </div>
              <div className={s.pairCompact}>
                {(['left', 'right'] as const).map((sideKey) => (
                  <NumberField
                    key={sideKey}
                    stacked
                    label={t(sideKey === 'left' ? m.segmentSliceStart : m.segmentSliceEnd)}
                    value={fieldOr(
                      eases.map((e) => sliceSide(e, sideKey)),
                      0,
                    )}
                    step={0.01}
                    min={0}
                    max={1}
                    precision={4}
                    helpId={`${FIELD.segmentSlice}.${sideKey}`}
                    onCommit={(v) => {
                      if (v.kind !== 'number') return;
                      const val = v.value;
                      const out = edit.commit(FIELD.segmentSlice, { key: m.txSlice }, (tx) =>
                        setSlice(tx, sideKey === 'left' ? { segments: ids, left: val } : { segments: ids, right: val }),
                      );
                      if (out.ok && out.value > 0) {
                        services.ui.toast(
                          { key: m.segmentSliceSkipped, params: { count: out.value } },
                          { kind: 'info' },
                        );
                      }
                    }}
                  />
                ))}
              </div>
            </>
          ) : (
            <Readout>{t(m.segmentSliceOnlyTween)}</Readout>
          )}
        </Row>
      )}

      {/* ---------------- Operations */}
      <Actions>
        <Button size="sm" icon="Grid3x3" command="segments.bake">
          {t(m.segmentBake)}
        </Button>
        <Button size="sm" icon="FlipHorizontal2" command="segments.reverse">
          {t(m.segmentReverse)}
        </Button>
        <Button size="sm" icon="TrendingUp" command="segments.extrapolate">
          {t(m.segmentExtrapolate)}
        </Button>
        <Button size="sm" icon="Link" command="segments.link">
          {t(m.segmentLink)}
        </Button>
      </Actions>
      {links.length > 0 && (
        <Callout
          tone="info"
          actions={
            <Button size="sm" variant="ghost" icon="Unlink" command="segments.unlink">
              {t(m.segmentUnlink)}
            </Button>
          }
        >
          {t(m.segmentLinked, { group: fmtList(locale, links.map(String)) })}
        </Callout>
      )}

      {/* ---------------- Exactness */}
      <Readout testId="exactness">
        {durBeats !== null && durSec !== null && unit !== 'speed'
          ? t(m.exactnessDuration, {
              phira: t(m.exactPhira, { state: phiraState }),
              rpe: t(m.exactRpe, { state: rpeState }),
              duration: t(m.segmentDuration, { beats: base.fmtBeat(durBeats), sec: fmtNum(durSec, 3) }),
            })
          : t(m.exactness, { phira: t(m.exactPhira, { state: phiraState }), rpe: t(m.exactRpe, { state: rpeState }) })}
      </Readout>
      {phiraNan && (
        <Callout
          tone="danger"
          actions={
            <Button
              size="sm"
              onClick={() => {
                edit.once({ key: m.txSlice }, (tx) => setSlice(tx, { segments: ids, left: 0, right: 1 }));
              }}
            >
              {t(m.useWholeCurve)}
            </Button>
          }
        >
          {t(m.nanSlice)}
        </Callout>
      )}
      {approx && (
        <Callout
          tone="info"
          testId="approx-split"
          actions={
            <Button size="sm" variant="ghost" command="segments.bake">
              {t(m.segmentBake)}
            </Button>
          }
        >
          {t(m.approxSplit)}
        </Callout>
      )}
      {differs.map((i) => (
        <Callout key={`d${String(i.ctx.key.id)}`} tone="info">
          {t(m.rpeDiffers, { id: i.ctx.key.ease.kind === 'tween' ? i.ctx.key.ease.id : 0 })}
        </Callout>
      ))}
      {baked && <Callout tone="warning">{t(m.bakedSpeed)}</Callout>}
      {ignored.length > 0 && (
        <Callout
          tone="info"
          testId="ignored-ease"
          actions={
            <Button
              size="sm"
              variant="ghost"
              icon="Settings2"
              onClick={() => {
                void services.ui.openDialog(DIALOG.projectSettings, { section: 'speed' });
              }}
            >
              {t(m.openSpeedSettings)}
            </Button>
          }
        >
          {t(m.ignoredEase, { id: ignored[0]?.ctx.key.src?.ignoredEase?.easingType ?? 0 })}
        </Callout>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// Section
// ---------------------------------------------------------------------------------------------------------------

export function SegmentSection(props: InspectorSectionProps) {
  const store = useStore(props.services);
  const ids = sortedIds(props.selection.segments);
  if (store === null || ids.length === 0) return null;
  return <SegmentSectionBody services={props.services} store={store} ids={ids} />;
}

function SegmentSectionBody(props: {
  readonly services: AppServices;
  readonly store: DocStore;
  readonly ids: readonly KeyId[];
}) {
  const t = useT();
  const infos = useKeyInfos(props.store, props.ids).filter((i) => i.ctx.next !== null);
  const base = useSectionBase(props.services, props.store);
  const first = infos[0];
  let detail = '';
  if (first !== undefined && infos.length === 1 && first.ctx.next !== null) {
    detail = t(m.readoutPair, {
      first: propName(t, first),
      second: t(m.segmentSpan, { from: base.fmtBeat(first.ctx.key.t), to: base.fmtBeat(first.ctx.next.t) }),
    });
  } else if (infos.length > 1) {
    detail = t(m.keysOnProps, { count: new Set(infos.map((i) => `${String(i.ctx.line)}:${String(i.ctx.prop)}`)).size });
  }
  return (
    <Section
      title={
        <Title
          main={infos.length === 1 ? t(m.segmentTitle) : t(m.segmentsTitle, { count: infos.length })}
          detail={detail}
        />
      }
    >
      <SegmentBody services={props.services} store={props.store} segments={props.ids} />
    </Section>
  );
}

/** "Rotation" / "Length" ... of a key's property, "Layer 2" suffix for additive layers. */
export function propName(t: ReturnType<typeof useT>, info: KeyInfo, withLayer = true): string {
  if (info.ctx.prop === null) return info.ctx.track.owner.kind === 'effectVar' ? info.ctx.track.owner.name : '';
  const name = t(propLabelKey(info.ctx.prop, info.kind, info.uiMover));
  if (!withLayer || info.ctx.layer === 0) return name;
  return `${name} (${info.layerName ?? t(m.layerN, { n: info.ctx.layer })})`;
}

export const segmentSectionDef: InspectorSectionDef = {
  id: 'segment',
  order: 200,
  level: 'guided',
  appliesTo: (sel) => sel.segments.size > 0,
  component: SegmentSection,
};
